/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateWorkflowRequest } from '../models/create-workflow-request';
import { GenericResponse } from '../models/generic-response';
import { GetWorkflowDelegatesResponse } from '../models/get-workflow-delegates-response';
import { GetWorkflowsResponse } from '../models/get-workflows-response';
import { UpdateWorkflowRequest } from '../models/update-workflow-request';
import { WorkflowResponse } from '../models/workflow-response';

/**
 * workflow apis
 */
@Injectable({ providedIn: 'root' })
export class WorkflowService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllWorkflows()` */
  static readonly GetAllWorkflowsPath = '/api/v1/workflows';

  /**
   * get all workflows.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllWorkflows()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWorkflows$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       * - name
       * - description
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetWorkflowsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.GetAllWorkflowsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetWorkflowsResponse>;
        })
      );
  }

  /**
   * get all workflows.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllWorkflows$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWorkflows(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       * - name
       * - description
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetWorkflowsResponse> {
    return this.getAllWorkflows$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetWorkflowsResponse>): GetWorkflowsResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createWorkflow()` */
  static readonly CreateWorkflowPath = '/api/v1/workflows';

  /**
   * create new workflow.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createWorkflow()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createWorkflow$Response(
    params: {
      body: CreateWorkflowRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkflowResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.CreateWorkflowPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkflowResponse>;
        })
      );
  }

  /**
   * create new workflow.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createWorkflow$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createWorkflow(
    params: {
      body: CreateWorkflowRequest;
    },
    context?: HttpContext
  ): Observable<WorkflowResponse> {
    return this.createWorkflow$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkflowResponse>): WorkflowResponse => r.body)
    );
  }

  /** Path part for operation `getWorkflowById()` */
  static readonly GetWorkflowByIdPath = '/api/v1/workflows/{id}';

  /**
   * get workflow by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWorkflowById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkflowById$Response(
    params: {
      /**
       * The workflow id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkflowResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.GetWorkflowByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkflowResponse>;
        })
      );
  }

  /**
   * get workflow by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWorkflowById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkflowById(
    params: {
      /**
       * The workflow id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkflowResponse> {
    return this.getWorkflowById$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkflowResponse>): WorkflowResponse => r.body)
    );
  }

  /** Path part for operation `disableWorkflowById()` */
  static readonly DisableWorkflowByIdPath = '/api/v1/workflows/{id}';

  /**
   * disable workflow by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableWorkflowById()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableWorkflowById$Response(
    params: {
      /**
       * The workflow id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.DisableWorkflowByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable workflow by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableWorkflowById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableWorkflowById(
    params: {
      /**
       * The workflow id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableWorkflowById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateWorkflowById()` */
  static readonly UpdateWorkflowByIdPath = '/api/v1/workflows/{id}';

  /**
   * update workflow by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateWorkflowById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateWorkflowById$Response(
    params: {
      /**
       * The workflow id that needs to be updated
       */
      id: number;
      body: UpdateWorkflowRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkflowResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.UpdateWorkflowByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkflowResponse>;
        })
      );
  }

  /**
   * update workflow by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateWorkflowById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateWorkflowById(
    params: {
      /**
       * The workflow id that needs to be updated
       */
      id: number;
      body: UpdateWorkflowRequest;
    },
    context?: HttpContext
  ): Observable<WorkflowResponse> {
    return this.updateWorkflowById$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkflowResponse>): WorkflowResponse => r.body)
    );
  }

  /** Path part for operation `getWorkflowDelegatesByUserId()` */
  static readonly GetWorkflowDelegatesByUserIdPath =
    '/api/v1/workflows/{id}/delegates';

  /**
   * get workflow delegates by user id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWorkflowDelegatesByUserId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkflowDelegatesByUserId$Response(
    params: {
      /**
       * The user id that needs to be fetch delegates
       */
      id: number;

      /**
       * This adds a filtering workflow-instance by category id
       */
      categoryId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetWorkflowDelegatesResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkflowService.GetWorkflowDelegatesByUserIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.query('categoryId', params.categoryId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetWorkflowDelegatesResponse>;
        })
      );
  }

  /**
   * get workflow delegates by user id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWorkflowDelegatesByUserId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkflowDelegatesByUserId(
    params: {
      /**
       * The user id that needs to be fetch delegates
       */
      id: number;

      /**
       * This adds a filtering workflow-instance by category id
       */
      categoryId?: number;
    },
    context?: HttpContext
  ): Observable<GetWorkflowDelegatesResponse> {
    return this.getWorkflowDelegatesByUserId$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetWorkflowDelegatesResponse>
        ): GetWorkflowDelegatesResponse => r.body
      )
    );
  }
}
